import { useEffect, useState, forwardRef, useContext } from "react";
import moment from "moment";
import { getTransactionQRCode } from "../../network/network";
import CurrencyFormatter from "../CurrencyFormatter";
import { AppContext } from "../../context/AppContextProvider";
import {
  TransactionType,
  AddonName,
  ZatcaInvoiceStatus,
} from "../../configs/constants";
import { useTranslation } from "react-i18next";
import { handleApiError } from "../../utils/errorHandler";

const getQRCodeSource = (qrCode) => {
  if (!qrCode) return "";

  const value = String(qrCode).trim();

  if (!value) return "";

  if (value.startsWith("data:image/")) return value;

  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("/") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (value.startsWith("<svg")) {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(value)}`;
  }

  if (value.startsWith("iVBORw0KGgo")) {
    return `data:image/png;base64,${value}`;
  }

  if (value.startsWith("/9j/")) {
    return `data:image/jpeg;base64,${value}`;
  }

  if (value.startsWith("R0lGOD")) {
    return `data:image/gif;base64,${value}`;
  }

  if (value.startsWith("PHN2Zy")) {
    return `data:image/svg+xml;base64,${value}`;
  }

  return "";
};

const TransactionSlip = forwardRef((props, ref) => {
  const transaction = props.transaction;
  const [localQRCode, setLocalQRCode] = useState("");
  const [qrLoading, setQrLoading] = useState(false);
  const [qrFetchFailed, setQrFetchFailed] = useState(false);
  const { authUser, appConfig } = useContext(AppContext);
  const { t } = useTranslation();
  const isRtl = appConfig?.direction === "rtl";
  const getLocalizedName = (item) =>
    isRtl
      ? item?.name_ar || item?.name || item?.name_en || ""
      : item?.name_en || item?.name || item?.name_ar || "";

  const getZatcaValue = (value) => {
    if (!value) return null;
    if (typeof value === "object") return value.value || value.id || value.name;
    return value;
  };

  const isZatcaAddonActive = authUser?.active_addons?.some(
    (addon) => addon?.id && addon?.name === AddonName.ZatcaEInvoicing,
  );
  const canShowZatcaData = !!(
    isZatcaAddonActive && transaction?.zatca?.is_zatca_invoice
  );
  const isB2B =
    (canShowZatcaData && transaction?.zatca?.is_b2b) ||
    !!transaction?.buyer_company_vat;
  const cashCollected = Number(transaction?.cash_collected ?? 0);
  const amountCharged = Number(transaction?.amount_charged ?? 0);
  const cashReturnAmount = Math.max(cashCollected - amountCharged, 0);

  const zatcaStampQR = canShowZatcaData
    ? transaction?.zatca?.qr_code || transaction?.zatca_qr_code || null
    : null;

  const zatcaStampQRSource = getQRCodeSource(zatcaStampQR);
  const finalQRSource = zatcaStampQRSource || getQRCodeSource(localQRCode);

  const zatcaStatus = canShowZatcaData
    ? getZatcaValue(transaction?.zatca?.status) ||
      transaction?.zatca_invoice_status ||
      null
    : null;
  const isRejected = zatcaStatus === ZatcaInvoiceStatus.Rejected;
  const isPending = [
    ZatcaInvoiceStatus.Pending,
    ZatcaInvoiceStatus.Submitted,
  ].includes(zatcaStatus);

  const styles = {
    container: { direction: isRtl ? "rtl" : "ltr", padding: "20px" },
    logo: {
      maxHeight: "120px",
      maxWidth: "200px",
      margin: "10px auto 20px",
    },
    divider: {
      borderBottom: "1px solid var(--color-black)",
      paddingTop: "10px",
    },
    dashedDivider: {
      borderBottom: "1px dashed var(--color-black)",
      paddingTop: "10px",
    },
    row: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "start",
      fontSize: "12px",
      paddingTop: "10px",
    },
    receiptHead: {
      borderBottom: "1px solid var(--color-black)",
      paddingTop: "10px",
      paddingBottom: "10px",
      fontWeight: "bold",
    },
    priceColumns: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    unitPrice: {
      width: "70px",
      textAlign: "center",
    },
    subtotalPrice: {
      width: "110px",
      textAlign: "left",
    },
    qrCode: {
      width: "140px",
      height: "140px",
      margin: "10px auto",
    },
    qrPlaceholder: {
      width: "140px",
      height: "140px",
      margin: "10px auto",
      border: "1px dashed var(--color-black)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 11,
      color: "var(--color-text)",
      padding: 4,
      textAlign: "center",
    },
    rejectedBadge: {
      display: "inline-block",
      padding: "2px 8px",
      border: "1px solid var(--color-danger-hover)",
      color: "var(--color-danger-hover)",
      fontSize: 11,
      borderRadius: 4,
      marginTop: 4,
    },
    buyerBlock: {
      borderTop: "1px solid var(--color-black)",
      borderBottom: "1px solid var(--color-black)",
      padding: "8px 0",
      marginTop: 6,
      fontSize: 12,
      fontWeight: 600,
    },
    buyerLine: {
      display: "flex",
      justifyContent: "space-between",
      paddingTop: 4,
      fontWeight: "normal",
    },
  };

  const paymentMethod =
    (transaction.type === TransactionType.Cash && t("cash")) ||
    (transaction.type === TransactionType.Credit && t("card")) ||
    (transaction.type === TransactionType.Multipayment && t("multi_payment"));

  useEffect(() => {
    if (!transaction?.id) return;
    if (zatcaStampQRSource) return;
    let isEffectActive = true;
    setQrLoading(true);
    setQrFetchFailed(false);
    getTransactionQRCode(transaction.id)
      .then((response) => {
        if (!isEffectActive) return;

        if (response?.data?.success === false) {
          handleApiError(response, t);
          setQrFetchFailed(true);
          return;
        }

        const qr = response?.data?.data?.qr_code || "";
        setLocalQRCode(qr);
        if (!qr) setQrFetchFailed(true);
      })
      .catch((error) => {
        if (isEffectActive) {
          handleApiError(error, t);
          setQrFetchFailed(true);
        }
      })
      .finally(() => {
        if (isEffectActive) setQrLoading(false);
      });
    return () => {
      isEffectActive = false;
    };
  }, [transaction?.id, zatcaStampQRSource, t]);

  const renderReceiptTitle = () => {
    if (transaction.reference_transaction) {
      return <span>{t("refund_invoice")}</span>;
    }
    if (authUser.is_vat_exempt) {
      return <span>{t("simplified_invoice")}</span>;
    }

    return isB2B ? (
      <span>{t("tax_invoice")}</span>
    ) : (
      <span>{t("simplified_tax_invoice")}</span>
    );
  };

  const renderPaymentMethodOrReference = () => {
    if (transaction.reference_transaction) {
      return (
        <div style={styles.row}>
          <span className="app-text-xs">
            {t("transaction_reference")}
          </span>
          <span>{transaction.reference_transaction?.uid}</span>
        </div>
      );
    }
    return (
      <div style={styles.row}>
        <span>{t("payment_method")}</span>
        <span>{paymentMethod}</span>
      </div>
    );
  };

  const renderDiscountSection = () => {
    if (transaction.discount && !transaction.reference_transaction) {
      return (
        <>
          <div style={styles.row}>
            <span>{t("subtotal")}</span>
            <span>
              <CurrencyFormatter value={transaction.sub_total} />
            </span>
          </div>
          <div style={styles.row}>
            <span>
              {t("discount")} % {transaction.discount.discount_percentage}
            </span>
            <span>
              <CurrencyFormatter value={transaction.discount_amount} />
            </span>
          </div>
          <div style={styles.divider} />
        </>
      );
    }
  };

  const renderCashPaymentDetails = () => {
    if (
      transaction.type === TransactionType.Cash &&
      !transaction.reference_transaction
    ) {
      return (
        <>
          <div style={styles.row}>
            <span>{t("amount_received")}</span>
            <span>
              <CurrencyFormatter value={cashCollected} />
            </span>
          </div>
          <div style={styles.row}>
            <span>{t("remaining")}</span>
            <span>
              <CurrencyFormatter value={cashReturnAmount} />
            </span>
          </div>
          <div style={styles.divider} />
        </>
      );
    }
  };

  const renderBuyerBlock = () => {
    if (!isB2B) return null;
    return (
      <div style={styles.buyerBlock}>
        <div>{t("buyer")}</div>
        <div style={styles.buyerLine}>
          <span>{t("buyer_company_name")}</span>
          <span>{transaction.buyer_company_name}</span>
        </div>
        <div style={styles.buyerLine}>
          <span>{t("buyer_vat")}</span>
          <span>{transaction.buyer_company_vat}</span>
        </div>
        {transaction.buyer_company_address && (
          <div style={styles.buyerLine}>
            <span>{t("address")}</span>
            <span>{transaction.buyer_company_address}</span>
          </div>
        )}
        {transaction.buyer_company_city && (
          <div style={styles.buyerLine}>
            <span>{t("city")}</span>
            <span>
              {transaction.buyer_company_district
                ? `${transaction.buyer_company_district} / `
                : ""}
              {transaction.buyer_company_city}
            </span>
          </div>
        )}
        {transaction.buyer_company_postal_code && (
          <div style={styles.buyerLine}>
            <span>{t("postal_code")}</span>
            <span>{transaction.buyer_company_postal_code}</span>
          </div>
        )}
      </div>
    );
  };

  // PDF: "If zatca_qr_code is null (ZATCA job hasn't finished yet for
  // B2C), print a placeholder 'Verifying with ZATCA...' and schedule a
  // re-print. Reprint should reuse the transaction's QR once available."
  const renderQRArea = () => {
    if (finalQRSource) {
      return (
        <img
          src={finalQRSource}
          alt={
            canShowZatcaData ? `${t("zatca")} ${t("qr_code")}` : t("qr_code")
          }
          style={styles.qrCode}
        />
      );
    }
    if (canShowZatcaData && (qrLoading || isPending || qrFetchFailed)) {
      return (
        <div style={styles.qrPlaceholder}>
          {t("verifying_with_zatca")}
        </div>
      );
    }
    return null;
  };

  // PDF: "Show a subtle badge only when rejected. For accepted /
  // accepted_with_warnings, don't clutter the receipt."
  const renderZatcaStatusBadge = () => {
    if (!canShowZatcaData || !isRejected) return null;
    return (
      <div className="app-text-center">
        <span style={styles.rejectedBadge}>{t("rejected_by_zatca")}</span>
      </div>
    );
  };

  return (
    <div id="slip" ref={ref} style={styles.container}>
      <center>
        {authUser.logo && (
          <>
            {/* logo area */}
            <img
              id="store-logo"
              style={styles.logo}
              src={authUser.logo}
              alt={t("business_logo")}
            />
            <br />
          </>
        )}
        <div>{authUser.business_name}</div>
        <div>{authUser.business_address}</div>
        <div>{authUser.branch_name}</div>
        <div>{authUser.is_vat_exempt === 1 ? "" : authUser.vat}</div>
        <div style={styles.divider} />

        {/* Receipt head */}
        <div style={styles.receiptHead}>
          {renderReceiptTitle()}
          <div style={{ fontWeight: "normal" }}>{transaction.uid}</div>
        </div>
      </center>

      {/* Receipt description */}
      {renderPaymentMethodOrReference()}

      {transaction.type !== TransactionType.Cash && (
        <div style={styles.row}>
          <span>{t("transaction_code")} #</span>
          <span>{transaction.reference}</span>
        </div>
      )}

      {/* B2B buyer block (replaces the inline name/VAT rows). For B2C
          we keep showing the simple buyer name row when present. */}
      {isB2B ? (
        renderBuyerBlock()
      ) : (
        <>
          {transaction.buyer_company_name && (
            <div style={styles.row}>
              <span>{t("buyer_name")}</span>
              <span>{transaction.buyer_company_name}</span>
            </div>
          )}
        </>
      )}

      <div style={styles.row}>
        <span>{t("issue_date")}</span>
        <span>
          {moment(transaction.created_at).format("MMM. D, YYYY hh:mm a")}
        </span>
      </div>

      {/* list area */}
      <div style={styles.divider} />
      <div style={styles.row}>
        <span>{t("item")}</span>
        <div style={styles.priceColumns}>
          <span style={styles.unitPrice}>{t("unit_price")}</span>
          <span style={styles.subtotalPrice}>{t("price_including_tax")}</span>
        </div>
      </div>
      <div style={styles.dashedDivider} />

      {transaction.items.map((item) => (
        <div style={styles.row} key={"product_items_" + item.id}>
          <span>
            {item.quantity} x {getLocalizedName(item)}
          </span>
          <div style={styles.priceColumns}>
            <span style={styles.unitPrice}>
              <CurrencyFormatter value={item.price} currencyCode={false} />
            </span>
            <span style={styles.subtotalPrice}>
              <CurrencyFormatter value={item.subtotal} currencyCode={false} />
            </span>
          </div>
        </div>
      ))}

      <div style={styles.divider} />

      {/* Receipt discount */}
      {renderDiscountSection()}

      {/* Receipt subtotal */}
      <div style={styles.row}>
        <span>{t("total_without_tax")}</span>
        <span>
          <CurrencyFormatter
            value={transaction.sub_total - transaction.discount_amount}
          />
        </span>
      </div>

      {/* Receipt tax */}
      <div style={styles.row}>
        <span>{t("tax")}</span>
        <span>
          <CurrencyFormatter value={transaction.tax} />
        </span>
      </div>

      <div style={styles.divider} />

      {/* Receipt total */}
      <div style={styles.row}>
        <span>
          {authUser.is_vat_exempt ? t("total") : t("total_with_tax")}
        </span>
        <span>
          <CurrencyFormatter value={transaction.amount_charged} />
        </span>
      </div>
      <div style={styles.divider} />

      {/* Receipt remain amount */}
      {renderCashPaymentDetails()}

      {/* QR area + status badge + legal disclosure */}
      <div className="app-text-center">
        {renderQRArea()}
        {renderZatcaStatusBadge()}
        <br />
        <small>{t("printed_by_anypos")}</small>
        <br />
      </div>
    </div>
  );
});

export default TransactionSlip;
